
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  BarChart3, 
  Users, 
  MessageSquare, 
  Activity,
  Database,
  Clock,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  ExternalLink,
  TestTube
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SystemMetrics {
  totalUsers: number;
  activeUsers: number;
  totalMessages: number;
  databaseSize: string;
  responseTime: number;
  uptime: string;
  errorRate: number;
  successRate: number;
}

interface AnalyticsData {
  metrics: SystemMetrics;
  trends: {
    userGrowth: number;
    messageGrowth: number;
    performanceScore: number;
  };
  alerts: Array<{
    type: 'warning' | 'error' | 'info';
    message: string;
    timestamp: string;
  }>;
}

const SystemAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  // Mock data generation - replace with real API calls
  const generateMockData = (): AnalyticsData => {
    return {
      metrics: {
        totalUsers: 1247,
        activeUsers: 89,
        totalMessages: 15623,
        databaseSize: '2.3 GB',
        responseTime: Math.floor(Math.random() * 100) + 50,
        uptime: '99.7%',
        errorRate: Math.random() * 2,
        successRate: 98.5 + Math.random() * 1.5
      },
      trends: {
        userGrowth: 12.5,
        messageGrowth: 8.3,
        performanceScore: 92 + Math.random() * 8
      },
      alerts: [
        {
          type: 'info',
          message: 'Database backup completed successfully',
          timestamp: new Date(Date.now() - 30 * 60 * 1000).toLocaleString()
        },
        {
          type: 'warning',
          message: 'Response time slightly elevated',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toLocaleString()
        }
      ]
    };
  };

  const loadAnalytics = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    const newData = generateMockData();
    setData(newData);
    setLastUpdated(new Date().toLocaleString());
    setLoading(false);
  };

  useEffect(() => {
    loadAnalytics();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (value: number, type: 'response' | 'error' | 'success') => {
    switch (type) {
      case 'response':
        return value < 100 ? 'text-green-500' : value < 200 ? 'text-yellow-500' : 'text-red-500';
      case 'error':
        return value < 1 ? 'text-green-500' : value < 3 ? 'text-yellow-500' : 'text-red-500';
      case 'success':
        return value > 98 ? 'text-green-500' : value > 95 ? 'text-yellow-500' : 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
    }
  };

  if (loading && !data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5 animate-spin text-sports-green" />
          <span className="text-white">Loading analytics data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">System Analytics Dashboard</h2>
          <p className="text-gray-400">Real-time system performance and usage metrics</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => navigate('/admin/test-dashboard')}
            variant="outline"
            className="border-sports-green text-sports-green hover:bg-sports-green hover:text-black"
          >
            <TestTube className="h-4 w-4 mr-2" />
            Test Dashboard
          </Button>
          <Button 
            onClick={loadAnalytics}
            disabled={loading}
            className="bg-sports-green text-black hover:bg-sports-green/80"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Users</p>
                <p className="text-2xl font-bold text-white">{data?.metrics.totalUsers.toLocaleString()}</p>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="h-3 w-3 text-green-500" />
                  <span className="text-green-500 text-xs">+{data?.trends.userGrowth}%</span>
                </div>
              </div>
              <Users className="h-8 w-8 text-sports-green" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Active Now</p>
                <p className="text-2xl font-bold text-white">{data?.metrics.activeUsers}</p>
                <Badge variant="secondary" className="mt-1 text-xs">
                  Online
                </Badge>
              </div>
              <Activity className="h-8 w-8 text-sports-green" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Messages</p>
                <p className="text-2xl font-bold text-white">{data?.metrics.totalMessages.toLocaleString()}</p>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="h-3 w-3 text-green-500" />
                  <span className="text-green-500 text-xs">+{data?.trends.messageGrowth}%</span>
                </div>
              </div>
              <MessageSquare className="h-8 w-8 text-sports-green" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Database Size</p>
                <p className="text-2xl font-bold text-white">{data?.metrics.databaseSize}</p>
                <p className="text-gray-400 text-xs mt-1">Storage used</p>
              </div>
              <Database className="h-8 w-8 text-sports-green" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-gray-900 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Activity className="h-5 w-5 text-sports-green" />
              Performance Metrics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-300">Response Time</span>
                <span className={`font-semibold ${getStatusColor(data?.metrics.responseTime || 0, 'response')}`}>
                  {data?.metrics.responseTime}ms
                </span>
              </div>
              <Progress value={Math.min((data?.metrics.responseTime || 0) / 2, 100)} className="h-2" />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-300">Success Rate</span>
                <span className={`font-semibold ${getStatusColor(data?.metrics.successRate || 0, 'success')}`}>
                  {data?.metrics.successRate.toFixed(1)}%
                </span>
              </div>
              <Progress value={data?.metrics.successRate || 0} className="h-2" />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-300">Error Rate</span>
                <span className={`font-semibold ${getStatusColor(data?.metrics.errorRate || 0, 'error')}`}>
                  {data?.metrics.errorRate.toFixed(2)}%
                </span>
              </div>
              <Progress value={data?.metrics.errorRate || 0} className="h-2" />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-300">Uptime</span>
                <span className="font-semibold text-green-500">{data?.metrics.uptime}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-sports-green" />
              System Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.alerts.map((alert, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg">
                  {getAlertIcon(alert.type)}
                  <div className="flex-1">
                    <p className="text-white text-sm">{alert.message}</p>
                    <p className="text-gray-400 text-xs mt-1">{alert.timestamp}</p>
                  </div>
                </div>
              ))}
              {(!data?.alerts || data.alerts.length === 0) && (
                <div className="text-center py-4">
                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-gray-400">No active alerts</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Health Overview */}
      <Card className="bg-gray-900 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-sports-green" />
            System Health Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">
                {data?.trends.performanceScore.toFixed(0)}%
              </div>
              <p className="text-gray-400 mb-3">Overall Performance</p>
              <Progress value={data?.trends.performanceScore || 0} className="h-3" />
            </div>
            
            <div className="space-y-3">
              <h4 className="font-semibold text-white">Quick Actions</h4>
              <div className="space-y-2">
                <Button
                  onClick={() => navigate('/admin/system-health')}
                  variant="outline"
                  size="sm"
                  className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  System Health
                </Button>
                <Button
                  onClick={() => navigate('/admin/database')}
                  variant="outline"
                  size="sm"
                  className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <Database className="h-4 w-4 mr-2" />
                  Database Tools
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-white">Last Updated</h4>
              <div className="flex items-center gap-2 text-gray-400">
                <Clock className="h-4 w-4" />
                <span className="text-sm">{lastUpdated}</span>
              </div>
              <p className="text-xs text-gray-500">
                Auto-refresh every 30 seconds
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemAnalytics;
